import {
  Volunteer,
  VolunteerListItem,
  VolunteerType,
} from '../interfaces/volunteer.interfaces';
import {
  Student,
  AssignedActivity,
  EmbeddedAccreditation,
  StatusHistoryEntry,
} from '../interfaces/student.interfaces';

export interface BackendVolunteer {
  _id: string;
  authId?: string;
  folio?: string;
  curp?: string;
  volunteerType: VolunteerType;
  accountStatus?: 'pending' | 'active' | 'inactive' | 'rejected';
  nombreCompleto: string;
  correoPersonal?: string;
  telefono: string;
  hasWhatsapp?: boolean;
  whatsappNumber?: string;
  hasSecondaryContact?: boolean;
  secondaryPhone?: string;
  fechaNacimiento: string;
  fechaInicioVoluntariado: string;
  occupation?: string;
  degrees?: any[];
  comoSupisteDeAbaq?: string;
  howDidYouHearAboutAbaq?: string;
  bioPersonal?: string;
  intereses?: string;
  talentos?: string;
  aportesAbaq?: string;
  animalExperience?: string;
  descExperiencia?: string;
  motivacionAbaq?: string;
  rankedActivities?: string[];
  facebookUsuario?: string;
  instagramUsuario?: string;
  tiktokUsuario?: string;
  xUsuario?: string;
  youtubeUsuario?: string;
  linkedinUsuario?: string;
  cartaAceptacion?: string;
  cloudUrls?: {
    ine?: string;
    cv?: string;
    comprobante?: string;
  };
  consents?: any;
  onboardingData?: any;
  assignedActivities?: any[];
  accreditations?: Array<{
    _id?: string;
    interactionFolio?: string;
    interactionType?: 'AC' | 'OP';
    type?: string;
    categoryCode?: string;
    status?: string;
    date?: string | Date;
    hoursAccredited?: number;
    hoursPerformed?: number;
    evidenceUrl?: string;
    comments?: string;
    performance?: string | null;
    performanceComment?: string;
  }>;
  statusHistory?: Array<{
    fromStatus?: string;
    toStatus?: string;
    reason?: string;
    comments?: string;
    changedByEmail?: string;
    date?: string | Date;
  }>;
  createdAt: string;
  updatedAt: string;
  fechaInicioFormularioExpediente?: string;
  fechaFinFormularioExpediente?: string;
  fechaEmisionExpediente?: string;
  fechaAceptacionExpediente?: string;
  fechaExpedicionExpediente?: string;
  tiempoLlenadoMinutosExpediente?: number;
}

export function mapVolunteer(
  volunteer: BackendVolunteer,
): Volunteer {
  const status = (volunteer.accountStatus || 'pending') as 'pending' | 'active' | 'inactive' | 'rejected';
  const onb = volunteer.onboardingData || {};
  return {
    id: volunteer._id,
    authId: volunteer.authId ?? '',
    folio: volunteer.folio || undefined,
    curp: volunteer.curp || onb.curp || '',
    volunteerType: volunteer.volunteerType,
    accountStatus: status,
    fullName: volunteer.nombreCompleto || volunteer.correoPersonal || 'Voluntario',
    mail: volunteer.correoPersonal ?? '',
    phone: volunteer.telefono,
    hasWhatsapp: volunteer.hasWhatsapp ?? onb.hasWhatsapp,
    whatsappNumber: volunteer.whatsappNumber ?? onb.whatsappNumber,
    hasSecondaryContact: volunteer.hasSecondaryContact ?? onb.hasSecondaryContact,
    secondaryPhone: volunteer.secondaryPhone ?? onb.secondaryPhone,
    birthDate: new Date(volunteer.fechaNacimiento),
    volunteerStartDate: new Date(
      volunteer.fechaInicioVoluntariado,
    ),
    occupation: volunteer.occupation || onb.occupation || '',
    degrees: volunteer.degrees && volunteer.degrees.length > 0 ? volunteer.degrees : (onb.degrees || []),
    howDidYouHearAboutAbaq:
      volunteer.comoSupisteDeAbaq ?? '',
    bioPersonal: volunteer.bioPersonal || onb.bioPersonal || '',
    intereses: volunteer.intereses || onb.intereses || '',
    talentos: volunteer.talentos || onb.talentos || '',
    aportesAbaq: volunteer.aportesAbaq || onb.aportesAbaq || '',
    animalExperience: volunteer.animalExperience || onb.animalExperience || '',
    descExperiencia: volunteer.descExperiencia || onb.descExperiencia || '',
    motivacionAbaq: volunteer.motivacionAbaq || onb.motivacionAbaq || '',
    rankedActivities: volunteer.rankedActivities && volunteer.rankedActivities.length > 0 ? volunteer.rankedActivities : (onb.rankedActivities || []),
    socialMedia: {
      facebook: volunteer.facebookUsuario ?? '',
      instagram: volunteer.instagramUsuario ?? '',
      tiktok: volunteer.tiktokUsuario ?? '',
      x: volunteer.xUsuario ?? '',
      youtube: volunteer.youtubeUsuario ?? '',
      linkedin: volunteer.linkedinUsuario ?? '',
    },
    assignedActivities:
      (volunteer.assignedActivities ?? []).map(
        (activity: any): AssignedActivity => ({
          activityRef:
            activity.activityRef?.toString() ?? '',
          title: activity.title,
          type: activity.type,
          comments: activity.comments,
          dueDate: activity.dueDate ? new Date(activity.dueDate) : undefined,
          valueInHours: activity.valueInHours,
          validatedHours: activity.validatedHours,
          assignedAt: activity.assignedAt ? new Date(activity.assignedAt) : undefined,
          interactionFolio: activity.interactionFolio,
          interactionType: activity.interactionType,
          status: activity.status ?? 'registered',
          performance: activity.performance ?? 'pending',
          performanceComment: activity.performanceComment ?? '',
        }),
      ),
    accreditations:
      (volunteer.accreditations ?? []).map(
        (acc): EmbeddedAccreditation => ({
          _id: acc._id,
          interactionFolio: acc.interactionFolio ?? '',
          interactionType: acc.interactionType ?? 'OP',
          type: acc.type ?? '',
          categoryCode: acc.categoryCode,
          status: acc.status ?? '',
          date: acc.date ? new Date(acc.date) : undefined,
          hoursAccredited: acc.hoursAccredited,
          hoursPerformed: acc.hoursPerformed,
          evidenceUrl: acc.evidenceUrl,
          comments: acc.comments,
          performance: acc.performance,
          performanceComment: acc.performanceComment,
        }),
      ),
    statusHistory:
      (volunteer.statusHistory ?? []).map(
        (entry): StatusHistoryEntry => ({
          fromStatus: entry.fromStatus,
          toStatus: entry.toStatus,
          reason: entry.reason,
          comments: entry.comments,
          changedByEmail: entry.changedByEmail,
          date: entry.date ? new Date(entry.date) : undefined,
        }),
      ),
    cartaAceptacion: volunteer.cartaAceptacion || onb.cartaAceptacion || '',
    cloudUrls: (volunteer as any).cloudUrls || onb.cloudUrls
      ? {
          ine: (volunteer as any).cloudUrls?.ine || onb.cloudUrls?.ine || '',
          cv: (volunteer as any).cloudUrls?.cv || onb.cloudUrls?.cv || '',
          comprobante: (volunteer as any).cloudUrls?.comprobante || onb.cloudUrls?.comprobante || '',
        }
      : { ine: '', cv: '', comprobante: '' },
    consents: volunteer.consents || onb.consents || {
      reglamento: false,
      privacidad: false,
      etica: false,
      incognito: false,
      consentimiento: false,
    },
    onboardingData: onb,
    created: new Date(volunteer.createdAt),
    updated: new Date(volunteer.updatedAt),
    fechaInicioFormularioExpediente: volunteer.fechaInicioFormularioExpediente ? new Date(volunteer.fechaInicioFormularioExpediente) : undefined,
    fechaFinFormularioExpediente: volunteer.fechaFinFormularioExpediente ? new Date(volunteer.fechaFinFormularioExpediente) : undefined,
    fechaEmisionExpediente: volunteer.fechaEmisionExpediente ? new Date(volunteer.fechaEmisionExpediente) : undefined,
    fechaAceptacionExpediente: volunteer.fechaAceptacionExpediente ? new Date(volunteer.fechaAceptacionExpediente) : undefined,
    fechaExpedicionExpediente: volunteer.fechaExpedicionExpediente ? new Date(volunteer.fechaExpedicionExpediente) : undefined,
    tiempoLlenadoMinutosExpediente: volunteer.tiempoLlenadoMinutosExpediente,
  };
}

export function mapVolunteerToListItem(
  volunteer: Volunteer,
): VolunteerListItem {
  return {
    id: volunteer.id,
    folio: volunteer.folio || undefined,
    fullName: volunteer.fullName || volunteer.mail || 'Voluntario',
    volunteerType: volunteer.volunteerType,
    mail: volunteer.mail,
    phone: volunteer.phone,
    volunteerStartDate:
      volunteer.volunteerStartDate,
    accountStatus: volunteer.accountStatus ?? 'pending',
  };
}

export function mapStudentToVolunteerListItem(
  student: Student,
): VolunteerListItem {
  const period =
    student.periods && student.periods.length > 0
      ? student.periods[0]
      : (student.periodStart && student.periodEnd
          ? {
              begin: student.periodStart,
              end: student.periodEnd,
              desiredHours: student.horasCubrir,
              validatedHours: student.horasAcreditadas,
            }
          : undefined);

  return {
    id: student.id,
    folio: student.folio || undefined,
    fullName: student.fullName || student.schoolMail || student.mail || 'Estudiante',
    volunteerType: 'SERVICE_SOCIAL',
    mail: student.schoolMail || student.mail || '',
    phone: student.phone,
    school: student.school,
    career: student.career,
    semester: student.semester,
    matricula: student.matricula,
    desiredHours: student.horasCubrir ?? period?.desiredHours ?? 0,
    validatedHours: student.horasAcreditadas ?? period?.validatedHours ?? 0,
    pendingHours: student.horasPendientes ?? 0,
    volunteerStartDate:
      period?.begin ??
      student.created ??
      new Date(),
    periodEnd:
      period?.end,
    accountStatus:
      student.accountStatus ?? 'pending',
  };
}
